/**
 * pdfDecrypt — make a PDF readable by pdf-lib before page-level operations.
 *
 * pdf-lib refuses ANY encrypted PDF ("Input document to PDFDocument.load is
 * encrypted"), including the very common owner-password-only kind that every
 * viewer opens without asking (payslips, bank statements, government forms).
 * Those PDFs load fine in PDF.js, so the Split/Extract page rendered
 * thumbnails and then died with "Split failed: …" the moment pdf-lib touched
 * the bytes. Loading with `ignoreEncryption: true` is NOT a fix — the content
 * streams stay encrypted and the copied pages come out blank.
 *
 * The lossless fix already exists in the product: qpdf unlock (cloud API
 * on web, native engine on desktop) via nativebridge.unlockPDF. With an EMPTY
 * password it authenticates owner-only documents and re-saves them
 * unencrypted. A document that needs a real user password fails that call and
 * surfaces as PdfLockedError so the page can point the user at Unlock.
 */
import { PDFDocument, EncryptedPDFError } from 'pdf-lib';
import { unlockPDF as nativeUnlock } from './nativebridge';

export class PdfLockedError extends Error {
  constructor(message = 'This PDF is password-protected.') {
    super(message);
    this.name = 'PdfLockedError';
    this.code = 'PDF_LOCKED';
  }
}

function toArrayBuffer(x) {
  if (x instanceof ArrayBuffer) return x;
  if (ArrayBuffer.isView(x)) return x.buffer.slice(x.byteOffset, x.byteOffset + x.byteLength);
  return null;
}

/** True when pdf-lib would refuse this buffer because it is encrypted. */
export async function isPdfLibEncrypted(arrayBuffer) {
  try {
    await PDFDocument.load(arrayBuffer, { updateMetadata: false });
    return false;
  } catch (err) {
    if (err instanceof EncryptedPDFError || /is encrypted/i.test(err?.message || '')) return true;
    throw err; // a genuinely broken PDF — let the caller report it as such
  }
}

/**
 * Return bytes pdf-lib can operate on. Unencrypted input is returned as-is.
 * Encrypted input is decrypted losslessly with an empty password; if that is
 * not possible (needs a user password / no engine reachable) throws
 * PdfLockedError.
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {{ unlock?: (bytes: ArrayBuffer, password: string) => Promise<ArrayBuffer|Uint8Array|null> }} [deps]
 */
export async function ensurePdfLibReadable(arrayBuffer, { unlock = nativeUnlock } = {}) {
  if (!(await isPdfLibEncrypted(arrayBuffer))) return arrayBuffer;

  let out = null;
  try {
    out = await unlock(arrayBuffer, '');
  } catch {
    out = null;
  }
  const buf = toArrayBuffer(out);
  if (!buf || buf.byteLength === 0) throw new PdfLockedError();

  // The engine returned bytes — make sure they are actually usable now.
  if (await isPdfLibEncrypted(buf)) throw new PdfLockedError();
  return buf;
}
